/**
 * UsageEventLogPage.tsx — Real-time billing event log with summary and graphs
 *
 * Features:
 *   - Period selector (1h, 6h, 24h, 7d, 30d, MTD, 3M, QTD, YTD, Year)
 *   - Summary cards: total events, total quantity, top event type, top customer
 *   - Daily spend bar graph
 *   - Customer filter dropdown
 *   - Event type filter
 *   - Paginated event table with drill-down
 *   - Defaults to MTD, customer-by-customer view
 */
import React, { useState, useMemo, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { useQuery } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { PrecisionToggle, usePrecisionMode } from '../components/PrecisionToggle';
import { formatCents } from '../utils/formatCents';

// ─── Period definitions ──────────────────────────────────────────────

type PeriodKey = '1h' | '6h' | '24h' | '7d' | '30d' | 'mtd' | '3m' | 'qtd' | 'ytd' | 'year';

interface PeriodDef { label: string; key: PeriodKey; }

const PERIODS: PeriodDef[] = [
  { label: '1 Hour', key: '1h' },
  { label: '6 Hours', key: '6h' },
  { label: '24 Hours', key: '24h' },
  { label: '7 Days', key: '7d' },
  { label: '30 Days', key: '30d' },
  { label: 'MTD', key: 'mtd' },
  { label: '3 Months', key: '3m' },
  { label: 'QTD', key: 'qtd' },
  { label: 'YTD', key: 'ytd' },
  { label: 'Year', key: 'year' },
];

function getPeriodRange(period: PeriodKey): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString();
  let from = new Date();

  switch (period) {
    case '1h': from.setHours(now.getHours() - 1); break;
    case '6h': from.setHours(now.getHours() - 6); break;
    case '24h': from.setDate(now.getDate() - 1); break;
    case '7d': from.setDate(now.getDate() - 7); break;
    case '30d': from.setDate(now.getDate() - 30); break;
    case 'mtd': from = new Date(now.getFullYear(), now.getMonth(), 1); break;
    case '3m': from.setMonth(now.getMonth() - 3); break;
    case 'qtd': {
      const qMonth = Math.floor(now.getMonth() / 3) * 3;
      from = new Date(now.getFullYear(), qMonth, 1);
      break;
    }
    case 'ytd': from = new Date(now.getFullYear(), 0, 1); break;
    case 'year': from.setFullYear(now.getFullYear() - 1); break;
  }

  return { from: from.toISOString(), to };
}

// ─── Types ───────────────────────────────────────────────────────────

interface UsageEvent {
  id: string;
  customer_id: string;
  customer_name: string | null;
  metric_name: string;
  quantity: number;
  properties: Record<string, unknown> | null;
  occurred_at: string;
  created_at: string;
  idempotency_key: string;
}

interface SummaryData {
  totals: { event_count: number; total_quantity: number };
  by_event_type: Array<{ event_type: string; event_count: number; total_quantity: number }>;
  by_customer: Array<{ customer_id: string; customer_name: string | null; event_count: number; total_quantity: number }>;
  daily: Array<{ date: string; event_count: number; total_quantity: number }>;
}

// ─── Hooks ───────────────────────────────────────────────────────────

function useUsageEventsSummary(from: string, to: string, customerId?: string) {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<SummaryData>({
    queryKey: ['usage-events-summary', from, to, customerId],
    queryFn: async () => {
      const params = new URLSearchParams({ from, to });
      if (customerId) params.set('customer_id', customerId);
      const res = await fetch(`${apiBaseUrl}/admin/usage-events/summary?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Summary failed: ${res.status}`);
      return (await res.json()).data;
    },
    refetchInterval: 30_000, // Auto-refresh every 30s
  });
}

function useUsageEvents(from: string, to: string, customerId: string | undefined, eventType: string | undefined, page: number, limit: number) {
  const { token, apiBaseUrl } = useAdminAuth();
  return useQuery<{ data: UsageEvent[]; meta: { total: number; page: number; limit: number; totalPages: number } }>({
    queryKey: ['usage-events', from, to, customerId, eventType, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({ from, to, page: String(page), limit: String(limit) });
      if (customerId) params.set('customer_id', customerId);
      if (eventType) params.set('event_type', eventType);
      const res = await fetch(`${apiBaseUrl}/admin/usage-events?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Events failed: ${res.status}`);
      return await res.json();
    },
    refetchInterval: 15_000, // Auto-refresh every 15s
  });
}

// ─── Helper: format quantity based on event type ─────────────────────

function formatQuantity(eventType: string, qty: number, precisionMode?: 'cents' | 'precision'): string {
  if (eventType === 'ai_invocation') return formatCents(qty, precisionMode);
  if (eventType === 'ai_completion' || eventType.includes('token')) return `${qty.toLocaleString()} tok`;
  return qty.toLocaleString();
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

// ─── Summary Card ────────────────────────────────────────────────────

function SummaryCard({ label, value, sublabel, color }: { label: string; value: string; sublabel?: string; color: string }): React.JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${color}`}>{value}</p>
      {sublabel && <p className="mt-0.5 text-xs text-slate-400">{sublabel}</p>}
    </div>
  );
}

// ─── Daily Spend Graph (simple CSS bar chart) ────────────────────────

function DailySpendGraph({ daily }: { daily: Array<{ date: string; event_count: number; total_quantity: number }> }): React.JSX.Element {
  const maxQty = Math.max(...daily.map((d) => d.total_quantity), 1);
  const sorted = [...daily].reverse().slice(-30); // Show last 30 days, oldest first

  if (sorted.length === 0) {
    return <p className="py-8 text-center text-sm text-slate-400">No data for this period</p>;
  }

  return (
    <div className="flex h-40 items-end gap-1 overflow-x-auto">
      {sorted.map((d) => {
        const heightPct = (d.total_quantity / maxQty) * 100;
        const date = new Date(d.date);
        return (
          <div key={d.date} className="group relative flex min-w-[20px] flex-1 flex-col items-center justify-end" style={{ height: '100%' }}>
            <div
              className="w-full rounded-t bg-gradient-to-t from-cyan-500 to-cyan-400 transition-all hover:from-cyan-600 hover:to-cyan-500"
              style={{ height: `${Math.max(heightPct, 2)}%` }}
              title={`${date.toLocaleDateString()}: ${formatNumber(d.total_quantity)} qty, ${d.event_count} events`}
            />
            <div className="absolute -top-8 hidden whitespace-nowrap rounded bg-slate-800 px-2 py-1 text-[10px] text-white group-hover:block z-10">
              {date.toLocaleDateString()} — {formatNumber(d.total_quantity)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Event Type Breakdown ────────────────────────────────────────────

function EventTypeBreakdown({ byType }: { byType: Array<{ event_type: string; event_count: number; total_quantity: number }> }): React.JSX.Element {
  const maxQty = Math.max(...byType.map((t) => t.total_quantity), 1);
  return (
    <div className="space-y-2">
      {byType.map((t) => (
        <div key={t.event_type} className="flex items-center gap-3">
          <span className="w-40 truncate font-mono text-xs text-slate-600">{t.event_type}</span>
          <div className="h-5 flex-1 overflow-hidden rounded bg-slate-100">
            <div
              className="h-full rounded bg-cyan-500"
              style={{ width: `${(t.total_quantity / maxQty) * 100}%` }}
            />
          </div>
          <span className="w-24 text-right text-xs tabular-nums text-slate-600">{formatNumber(t.total_quantity)}</span>
          <span className="w-16 text-right text-xs text-slate-400">{t.event_count} evt</span>
        </div>
      ))}
    </div>
  );
}

// ─── Customer Breakdown ──────────────────────────────────────────────

function CustomerBreakdown({ byCustomer }: { byCustomer: Array<{ customer_id: string; customer_name: string | null; event_count: number; total_quantity: number }> }): React.JSX.Element {
  const maxQty = Math.max(...byCustomer.map((c) => c.total_quantity), 1);
  return (
    <div className="space-y-2">
      {byCustomer.slice(0, 10).map((c) => (
        <div key={c.customer_id} className="flex items-center gap-3">
          <span className="w-32 truncate text-xs text-slate-600">{c.customer_name ?? c.customer_id.slice(0, 8)}</span>
          <div className="h-5 flex-1 overflow-hidden rounded bg-slate-100">
            <div
              className="h-full rounded bg-purple-500"
              style={{ width: `${(c.total_quantity / maxQty) * 100}%` }}
            />
          </div>
          <span className="w-24 text-right text-xs tabular-nums text-slate-600">{formatNumber(c.total_quantity)}</span>
          <span className="w-16 text-right text-xs text-slate-400">{c.event_count} evt</span>
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────

export default function UsageEventLogPage(): React.JSX.Element {
  const [period, setPeriod] = useState<PeriodKey>('mtd');
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [eventType, setEventType] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [selectedEvent, setSelectedEvent] = useState<UsageEvent | null>(null);
  const precisionMode = usePrecisionMode();

  const { from, to } = useMemo(() => getPeriodRange(period), [period]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [period, customerId, eventType]);

  const summaryQuery = useUsageEventsSummary(from, to, customerId);
  const eventsQuery = useUsageEvents(from, to, customerId, eventType, page, limit);

  const summary = summaryQuery.data;
  const events = eventsQuery.data;

  // Extract unique event types from summary for filter dropdown
  const eventTypes = summary?.by_event_type?.map((t) => t.event_type) ?? [];

  // Calculate cost estimate (sum of ai_invocation quantities = cents)
  const totalCostCents = summary?.by_event_type
    ?.find((t) => t.event_type === 'ai_invocation')
    ?.total_quantity ?? 0;

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl px-4 py-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Billing Event Log</h1>
            <p className="mt-1 text-sm text-slate-500">Real-time usage events driving billing charges</p>
          </div>
          <div className="flex items-center gap-3">
            <PrecisionToggle />
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <span className={`inline-block h-2 w-2 rounded-full ${summaryQuery.isFetching ? 'bg-amber-400' : 'bg-green-400'}`} />
              {summaryQuery.isFetching ? 'Updating…' : 'Live'} · auto-refresh 30s
            </div>
          </div>
        </div>

        {/* Period Selector */}
        <div className="mb-4 flex flex-wrap gap-2">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition ${
                period === p.key
                  ? 'bg-cyan-500 text-white shadow-sm'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <select
            value={customerId ?? ''}
            onChange={(e) => setCustomerId(e.target.value || undefined)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
          >
            <option value="">All Customers</option>
            {summary?.by_customer?.map((c) => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.customer_name ?? c.customer_id.slice(0, 8)}
              </option>
            ))}
          </select>

          <select
            value={eventType ?? ''}
            onChange={(e) => setEventType(e.target.value || undefined)}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
          >
            <option value="">All Event Types</option>
            {eventTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          {(customerId || eventType) && (
            <button
              onClick={() => { setCustomerId(undefined); setEventType(undefined); }}
              className="text-xs text-slate-500 underline hover:text-slate-700"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
          <SummaryCard
            label="Total Events"
            value={formatNumber(summary?.totals?.event_count ?? 0)}
            sublabel={period === 'mtd' ? 'Month to date' : PERIODS.find((p) => p.key === period)?.label}
            color="text-slate-900"
          />
          <SummaryCard
            label="Total Quantity"
            value={formatNumber(summary?.totals?.total_quantity ?? 0)}
            sublabel="Across all event types"
            color="text-cyan-600"
          />
          <SummaryCard
            label="AI Cost (billed)"
            value={formatCents(totalCostCents, precisionMode)}
            sublabel="From ai_invocation events"
            color="text-red-600"
          />
          <SummaryCard
            label="Top Customer"
            value={summary?.by_customer?.[0]?.customer_name ?? summary?.by_customer?.[0]?.customer_id?.slice(0, 8) ?? '—'}
            sublabel={summary?.by_customer?.[0] ? `${formatNumber(summary.by_customer[0].total_quantity)} qty` : undefined}
            color="text-purple-600"
          />
        </div>

        {/* Graphs */}
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Daily Spend (Quantity)</h3>
            <DailySpendGraph daily={summary?.daily ?? []} />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">By Event Type</h3>
            <EventTypeBreakdown byType={summary?.by_event_type ?? []} />
          </div>
        </div>

        {/* Customer Breakdown (only when no customer filter) */}
        {!customerId && summary && summary.by_customer.length > 0 && (
          <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Top Customers by Quantity</h3>
            <CustomerBreakdown byCustomer={summary.by_customer} />
          </div>
        )}

        {/* Event Table */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-700">
              Billing Events {events?.meta?.total != null && `(${formatNumber(events.meta.total)} total)`}
            </h3>
            {eventsQuery.isFetching && <span className="text-xs text-amber-500">Updating…</span>}
          </div>

          {eventsQuery.isLoading && (
            <p className="px-4 py-8 text-center text-sm text-slate-400">Loading events…</p>
          )}

          {!eventsQuery.isLoading && events && events.data.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-slate-400">No billing events found for this period.</p>
          )}

          {events && events.data.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-2 font-medium">Time</th>
                      <th className="px-4 py-2 font-medium">Customer</th>
                      <th className="px-4 py-2 font-medium">Event Type</th>
                      <th className="px-4 py-2 text-right font-medium">Quantity</th>
                      <th className="px-4 py-2 font-medium">Model</th>
                      <th className="px-4 py-2 font-medium">Correlation ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.data.map((evt) => {
                      const props = evt.properties ?? {};
                      const model = (props.model as string) ?? '—';
                      const corrId = (props.correlation_id as string) ?? '—';
                      return (
                        <tr
                          key={evt.id}
                          onClick={() => setSelectedEvent(evt)}
                          className="cursor-pointer border-b border-slate-50 transition hover:bg-slate-50"
                        >
                          <td className="px-4 py-2 text-xs text-slate-500">
                            {new Date(evt.occurred_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-2 text-xs text-slate-700">
                            {evt.customer_name ?? evt.customer_id.slice(0, 8)}
                          </td>
                          <td className="px-4 py-2">
                            <span className="rounded bg-cyan-50 px-2 py-0.5 font-mono text-[11px] text-cyan-700">
                              {evt.metric_name}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-xs tabular-nums text-slate-700">
                            {formatQuantity(evt.metric_name, evt.quantity, precisionMode)}
                          </td>
                          <td className="px-4 py-2 font-mono text-[11px] text-slate-500">{model}</td>
                          <td className="px-4 py-2 font-mono text-[10px] text-slate-400">{corrId?.slice(0, 16)}…</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
                <p className="text-xs text-slate-500">
                  Page {events.meta.page} of {events.meta.totalPages || 1} · {formatNumber(events.meta.total)} events
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    className="rounded-md border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  >
                    ← Prev
                  </button>
                  <button
                    onClick={() => setPage(Math.min(events.meta.totalPages, page + 1))}
                    disabled={page >= events.meta.totalPages}
                    className="rounded-md border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Event Detail Modal */}
        {selectedEvent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setSelectedEvent(null)}>
            <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">Event Detail</h3>
                <button onClick={() => setSelectedEvent(null)} className="text-slate-400 hover:text-slate-600">✕</button>
              </div>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium uppercase text-slate-500">Event Type</p>
                    <p className="font-mono text-slate-900">{selectedEvent.metric_name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-slate-500">Quantity</p>
                    <p className="font-mono text-slate-900">{formatQuantity(selectedEvent.metric_name, selectedEvent.quantity, precisionMode)}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-slate-500">Customer</p>
                    <p className="text-slate-900">{selectedEvent.customer_name ?? selectedEvent.customer_id}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-slate-500">Occurred At</p>
                    <p className="text-slate-900">{new Date(selectedEvent.occurred_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase text-slate-500">Idempotency Key</p>
                    <p className="font-mono text-xs text-slate-600 break-all">{selectedEvent.idempotency_key}</p>
                  </div>
                </div>
                <div>
                  <p className="mb-1 text-xs font-medium uppercase text-slate-500">Properties</p>
                  <pre className="overflow-x-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-700">
                    {JSON.stringify(selectedEvent.properties, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
